import { useState, useEffect } from 'react'
import { passwordCache } from '../utils/passwordCache'
import type { Shipment, ShipmentItem } from '../types'

interface ManufacturingInfo {
  name: string
  address: string
  city: string
  state: string
  country: string
  zip: string
}

interface ManualLineItem {
  id: string
  sku?: string
  description: string
  quantity: number
  unitValue: number
  currency: 'CAD' | 'USD'
  countryOfOrigin: string
  htsCode: string
  manufacturingInfo?: ManufacturingInfo
}

interface InvoiceEditorDialogProps {
  isOpen: boolean
  onClose: () => void
  shipment: Shipment
  items: ShipmentItem[]
  onGenerate: (manualItems: ManualLineItem[]) => Promise<void>
  ltlType: 'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo'
  brokerType: 'clearit' | 'acb' | 'jas'
  fcId: string
  fcAddress: { street: string; city: string; state: string; zip: string }
  setLtlType: (type: 'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo') => void
  setBrokerType: (type: 'clearit' | 'acb' | 'jas') => void
  setFcId: (id: string) => void
  setFcAddress: (address: { street: string; city: string; state: string; zip: string }) => void
  invoicePassword: string
  setInvoicePassword: (password: string) => void
  generatingInvoice: boolean
  invoiceError: string | null
}

export default function InvoiceEditorDialog({
  isOpen,
  onClose,
  shipment,
  items,
  onGenerate,
  ltlType,
  brokerType,
  fcId,
  fcAddress,
  setLtlType,
  setBrokerType,
  setFcId,
  setFcAddress,
  invoicePassword,
  setInvoicePassword,
  generatingInvoice,
  invoiceError
}: InvoiceEditorDialogProps) {
  const [manualItems, setManualItems] = useState<ManualLineItem[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [includeManufacturing, setIncludeManufacturing] = useState(false)
  const [cachedPasswordMinutes, setCachedPasswordMinutes] = useState(0)

  // Check for cached password on mount
  useEffect(() => {
    if (passwordCache.hasValidCache()) {
      setCachedPasswordMinutes(passwordCache.getRemainingMinutes())
    }
  }, [])

  // Form state for new manual item
  const [newItem, setNewItem] = useState<ManualLineItem>({
    id: '',
    sku: '',
    description: '',
    quantity: 1,
    unitValue: 0,
    currency: 'CAD',
    countryOfOrigin: '',
    htsCode: '',
    manufacturingInfo: undefined
  })

  const handleAddItem = () => {
    if (!newItem.description.trim() || newItem.unitValue <= 0 || newItem.quantity <= 0) {
      alert('Please fill in required fields: description, quantity, and unit value')
      return
    }

    const itemToAdd = {
      ...newItem,
      id: `manual-${Date.now()}`,
      manufacturingInfo: includeManufacturing ? newItem.manufacturingInfo : undefined
    }

    setManualItems([...manualItems, itemToAdd])

    // Reset form
    setNewItem({
      id: '',
      sku: '',
      description: '',
      quantity: 1,
      unitValue: 0,
      currency: 'CAD',
      countryOfOrigin: '',
      htsCode: '',
      manufacturingInfo: undefined
    })
    setIncludeManufacturing(false)
    setShowAddForm(false)
  }

  const handleRemoveItem = (id: string) => {
    setManualItems(manualItems.filter(item => item.id !== id))
  }

  const handleGenerateInvoice = async () => {
    await onGenerate(manualItems)
  }

  const calculateManualTotal = () => {
    return manualItems.reduce((sum, item) => sum + (item.unitValue * item.quantity), 0)
  }

  const calculateShipmentTotal = () => {
    return items.reduce((sum, item) => sum + ((item.declaredValue || 0) * item.quantity), 0)
  }

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      overflowY: 'auto',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}>
        <h3 style={{ margin: '0 0 20px 0' }}>
          Commercial Invoice Editor
        </h3>

        {invoiceError && (
          <div style={{
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            color: '#721c24',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            {invoiceError}
          </div>
        )}

        {/* Broker Selection */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            Broker Information
          </label>
          <select
            value={brokerType}
            onChange={(e) => setBrokerType(e.target.value as 'clearit' | 'acb' | 'jas')}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="clearit">ClearIT Broker Information</option>
            <option value="acb">ACB Broker Information</option>
            <option value="jas">JAS Forwarding — Lynn E. Reed</option>
          </select>
        </div>

        {/* LTL Type Selection */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            LTL Type
          </label>
          <select
            value={ltlType}
            onChange={(e) => {
              setLtlType(e.target.value as 'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo')
              if (e.target.value !== 'direct_amazon') {
                setFcId('')
                setFcAddress({ street: '', city: '', state: '', zip: '' })
              }
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="direct_amazon">Direct to Amazon LTL</option>
            <option value="pattern_middle_mile">Pattern Middle Mile LTL</option>
            <option value="ups_buffalo">UPS Buffalo Warehouse</option>
          </select>
          <div style={{
            fontSize: '12px',
            color: '#666',
            marginTop: '4px'
          }}>
            {ltlType === 'pattern_middle_mile'
              ? 'Destination: Pattern Middle Mile - Hebron Warehouse, 2250 Progress Dr, Hebron, KY 41048'
              : ltlType === 'ups_buffalo'
              ? 'Destination: UPS Warehouse (EIN 58-2480149), 1907 James E Casey Drive, Buffalo, New York 14206, Tel: +1 888-742-5877'
              : 'Destination: Amazon Fulfillment Center'}
          </div>
        </div>

        {/* FC Info for Direct to Amazon */}
        {ltlType === 'direct_amazon' && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                Amazon FC ID <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={fcId}
                onChange={(e) => setFcId(e.target.value.toUpperCase())}
                placeholder="e.g., MDW2, ONT8, etc."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                FC Address <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={fcAddress.street}
                onChange={(e) => setFcAddress({ ...fcAddress, street: e.target.value })}
                placeholder="Street Address"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                <input
                  type="text"
                  value={fcAddress.city}
                  onChange={(e) => setFcAddress({ ...fcAddress, city: e.target.value })}
                  placeholder="City"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="text"
                  value={fcAddress.state}
                  onChange={(e) => setFcAddress({ ...fcAddress, state: e.target.value.toUpperCase() })}
                  placeholder="State"
                  maxLength={2}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="text"
                  value={fcAddress.zip}
                  onChange={(e) => setFcAddress({ ...fcAddress, zip: e.target.value })}
                  placeholder="ZIP"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* Shipment Items Summary */}
        <div style={{
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>
            Shipment Items ({items.length})
          </h4>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Total Value: ${calculateShipmentTotal().toFixed(2)} CAD
          </div>
        </div>

        {/* Manual Items Section */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ margin: 0, fontSize: '16px' }}>
              Additional Manual Items ({manualItems.length})
            </h4>
            <button
              className="button secondary"
              onClick={() => setShowAddForm(!showAddForm)}
              style={{ padding: '6px 12px', fontSize: '14px' }}
            >
              {showAddForm ? 'Cancel' : '+ Add Item'}
            </button>
          </div>

          {/* Manual Items List */}
          {manualItems.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {manualItems.map((item) => (
                <div key={item.id} style={{
                  padding: '12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  backgroundColor: '#fff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {item.description}
                      </div>
                      {item.sku && (
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '2px' }}>
                          SKU/ASIN: {item.sku}
                        </div>
                      )}
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        Qty: {item.quantity} × ${item.unitValue.toFixed(2)} {item.currency} = ${(item.quantity * item.unitValue).toFixed(2)} {item.currency}
                      </div>
                      {item.countryOfOrigin && (
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          Origin: {item.countryOfOrigin} {item.htsCode && `| HTS: ${item.htsCode}`}
                        </div>
                      )}
                      {item.manufacturingInfo && (
                        <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                          Manufacturer: {item.manufacturingInfo.name}, {item.manufacturingInfo.city}, {item.manufacturingInfo.country}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <div style={{
                padding: '8px',
                backgroundColor: '#e7f3ff',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                textAlign: 'right'
              }}>
                Manual Items Total: ${calculateManualTotal().toFixed(2)} CAD
              </div>
            </div>
          )}

          {/* Add Item Form */}
          {showAddForm && (
            <div style={{
              padding: '16px',
              border: '2px solid #007bff',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa'
            }}>
              <h5 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>Add New Line Item</h5>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                  SKU / ASIN (Optional)
                </label>
                <input
                  type="text"
                  value={newItem.sku || ''}
                  onChange={(e) => setNewItem({ ...newItem, sku: e.target.value })}
                  placeholder="e.g., B07XYZ1234, SKU-12345"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                  Description <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  placeholder="Product description"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px',
                    minHeight: '60px'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                    Quantity <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                    Unit Value <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newItem.unitValue}
                    onChange={(e) => setNewItem({ ...newItem, unitValue: parseFloat(e.target.value) || 0 })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                    Currency
                  </label>
                  <select
                    value={newItem.currency}
                    onChange={(e) => setNewItem({ ...newItem, currency: e.target.value as 'CAD' | 'USD' })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  >
                    <option value="CAD">CAD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                    Country of Origin
                  </label>
                  <input
                    type="text"
                    value={newItem.countryOfOrigin}
                    onChange={(e) => setNewItem({ ...newItem, countryOfOrigin: e.target.value.toUpperCase() })}
                    placeholder="e.g., CN, US, CA"
                    maxLength={3}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                    HTS Code
                  </label>
                  <input
                    type="text"
                    value={newItem.htsCode}
                    onChange={(e) => setNewItem({ ...newItem, htsCode: e.target.value })}
                    placeholder="e.g., 8471.30.01"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              {/* Manufacturing Information Toggle */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeManufacturing}
                    onChange={(e) => setIncludeManufacturing(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '13px', fontWeight: 'bold' }}>
                    Include Manufacturing Information (Optional)
                  </span>
                </label>
              </div>

              {/* Manufacturing Fields */}
              {includeManufacturing && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  marginBottom: '12px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Manufacturer Name
                    </label>
                    <input
                      type="text"
                      value={newItem.manufacturingInfo?.name || ''}
                      onChange={(e) => setNewItem({
                        ...newItem,
                        manufacturingInfo: {
                          ...newItem.manufacturingInfo,
                          name: e.target.value,
                          address: newItem.manufacturingInfo?.address || '',
                          city: newItem.manufacturingInfo?.city || '',
                          state: newItem.manufacturingInfo?.state || '',
                          country: newItem.manufacturingInfo?.country || '',
                          zip: newItem.manufacturingInfo?.zip || ''
                        }
                      })}
                      placeholder="Company name"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                      Address
                    </label>
                    <input
                      type="text"
                      value={newItem.manufacturingInfo?.address || ''}
                      onChange={(e) => setNewItem({
                        ...newItem,
                        manufacturingInfo: {
                          ...newItem.manufacturingInfo!,
                          address: e.target.value
                        }
                      })}
                      placeholder="Street address"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                        City
                      </label>
                      <input
                        type="text"
                        value={newItem.manufacturingInfo?.city || ''}
                        onChange={(e) => setNewItem({
                          ...newItem,
                          manufacturingInfo: {
                            ...newItem.manufacturingInfo!,
                            city: e.target.value
                          }
                        })}
                        placeholder="City"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                        State
                      </label>
                      <input
                        type="text"
                        value={newItem.manufacturingInfo?.state || ''}
                        onChange={(e) => setNewItem({
                          ...newItem,
                          manufacturingInfo: {
                            ...newItem.manufacturingInfo!,
                            state: e.target.value
                          }
                        })}
                        placeholder="State"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                        Country
                      </label>
                      <input
                        type="text"
                        value={newItem.manufacturingInfo?.country || ''}
                        onChange={(e) => setNewItem({
                          ...newItem,
                          manufacturingInfo: {
                            ...newItem.manufacturingInfo!,
                            country: e.target.value.toUpperCase()
                          }
                        })}
                        placeholder="Country"
                        maxLength={3}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 'bold' }}>
                        ZIP
                      </label>
                      <input
                        type="text"
                        value={newItem.manufacturingInfo?.zip || ''}
                        onChange={(e) => setNewItem({
                          ...newItem,
                          manufacturingInfo: {
                            ...newItem.manufacturingInfo!,
                            zip: e.target.value
                          }
                        })}
                        placeholder="ZIP"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  className="button secondary"
                  onClick={() => setShowAddForm(false)}
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  className="button"
                  onClick={handleAddItem}
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                >
                  Add Item
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Password Field */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            Password
            {cachedPasswordMinutes > 0 && (
              <span style={{ fontWeight: 'normal', color: '#28a745', marginLeft: '8px', fontSize: '12px' }}>
                (cached for {cachedPasswordMinutes} min)
              </span>
            )}
          </label>
          <input
            type="password"
            value={invoicePassword}
            onChange={(e) => setInvoicePassword(e.target.value)}
            placeholder="Enter password to generate invoice"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: cachedPasswordMinutes > 0 ? '1px solid #28a745' : '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: cachedPasswordMinutes > 0 ? '#f8fff8' : '#fff'
            }}
          />
        </div>

        {/* Grand Total */}
        <div style={{
          padding: '16px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>
            Invoice Grand Total
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
            Shipment Items: ${calculateShipmentTotal().toFixed(2)} CAD
          </div>
          {manualItems.length > 0 && (
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
              Manual Items: ${calculateManualTotal().toFixed(2)} CAD
            </div>
          )}
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#155724', marginTop: '8px', paddingTop: '8px', borderTop: '2px solid #c3e6cb' }}>
            Total: ${(calculateShipmentTotal() + calculateManualTotal()).toFixed(2)} CAD
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            className="button secondary"
            onClick={() => {
              onClose()
              setManualItems([])
              setShowAddForm(false)
            }}
            disabled={generatingInvoice}
          >
            Cancel
          </button>
          <button
            className="button"
            onClick={handleGenerateInvoice}
            disabled={generatingInvoice || !invoicePassword.trim()}
          >
            {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
          </button>
        </div>
      </div>
    </div>
  )
}
